import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { createSearchParamsBatcher, nextSearchParams, parseUrlValue } from "./url_state.js";
/**
 * One for the whole app, not one per hook: the writes that collide are two
 * different `useUrlState` calls — a filter and its page — in one handler.
 */
const batchWrite = createSearchParamsBatcher();
/**
 * `useState` for anything the user can see, held in the URL instead.
 *
 * ## Why this is a rule and not a preference
 *
 * State parked in component memory resets on F5, on the back button, and on
 * any link the user tries to share. None of those throw, so none of them look
 * like bugs from the inside — but all three read as bugs to the person using
 * the app, who pressed refresh and lost their place. The URL is the only store
 * that survives all three for free.
 *
 * Applies to filters, sort, tab, view mode, selected row, open dialog,
 * pagination and search text. It does NOT apply to transient UI —
 * `isSubmitting`, an error string, a menu's anchor element — which is not
 * state the user navigates to, and it does not apply to **unsaved form input**:
 * a half-typed phone number in the URL is a privacy regression, because URLs
 * are kept in browser history, in logs, and in whatever the user pastes into a
 * chat window.
 *
 * ## Why validated
 *
 * A URL is user input. `?view=nonsense` is one keystroke away from any user
 * and is guaranteed from any bot, and a component that renders whatever the
 * query string says will eventually render `undefined` into a switch that has
 * no default. The schema turns that into the default value silently, which is
 * the right answer: a bad parameter should degrade the view, never break it.
 *
 * ## Why `replace`
 *
 * Toggling card/table three times must not put three entries in the history
 * stack for the back button to walk through. `replace: true` keeps "back"
 * meaning "the page I came from" rather than "the last thing I clicked". The
 * state still survives F5 and still travels in a copied link, which is the
 * whole point; only the undo stack is spared.
 *
 * ## Path param or query param
 *
 * This hook is the QUERY-param half. Use a path segment instead when the state
 * names a distinct thing the user navigated to — `/voice-sessions/new/phone`
 * is a different task from `/new/phone-mock` and deserves its own URL. Use
 * this when the state only changes how one page is drawn.
 *
 * This app routes with react-router v7, so `useSearchParams` is the store.
 * There is no TanStack Router `validateSearch` here. The two decisions worth
 * testing live in `utils/url_state.ts`, because the unit test config runs in
 * `node` with no DOM and anything inside this hook body is untested by
 * construction.
 */
export function useUrlState(key, schema, fallback) {
    const [searchParams, setSearchParams] = useSearchParams();
    const raw = searchParams.get(key);
    const value = useMemo(() => parseUrlValue(raw, schema, fallback), [raw, schema, fallback]);
    const setValue = useCallback((next) => {
        setSearchParams((current) => batchWrite(current, (latest) => nextSearchParams(latest, key, next, fallback)), { replace: true });
    }, [key, fallback, setSearchParams]);
    return [value, setValue];
}
//# sourceMappingURL=use_url_state.js.map